/**
 * Level-filtered logger. Production builds default to `warn` so the console stays clean.
 * Three-free and safe in Node.
 */

export const LEVELS = { silent: 0, error: 1, warn: 2, info: 3, debug: 4, trace: 5 };

export class Logger {
  constructor(level = 'warn') {
    this.level = LEVELS[level] ?? LEVELS.warn;
    /** Bounded capture of everything logged, for the dev overlay and tool reports. */
    this.buffer = [];
    this.maxBuffer = 256;
    /** Names already warned about, so a per-frame warning prints once. */
    this._once = new Set();
  }

  setLevel(level) {
    this.level = LEVELS[level] ?? this.level;
  }

  _write(kind, args) {
    if (this.buffer.length >= this.maxBuffer) this.buffer.shift();
    this.buffer.push({ kind, text: args.map(stringify).join(' ') });
  }

  error(...a) {
    if (this.level >= LEVELS.error) {
      this._write('error', a);
      console.error('[coi]', ...a);
    }
  }

  warn(...a) {
    if (this.level >= LEVELS.warn) {
      this._write('warn', a);
      console.warn('[coi]', ...a);
    }
  }

  info(...a) {
    if (this.level >= LEVELS.info) {
      this._write('info', a);
      console.info('[coi]', ...a);
    }
  }

  debug(...a) {
    if (this.level >= LEVELS.debug) {
      this._write('debug', a);
      console.log('[coi]', ...a);
    }
  }

  trace(...a) {
    if (this.level >= LEVELS.trace) {
      this._write('trace', a);
      console.log('[coi:trace]', ...a);
    }
  }

  /** Logs a warning at most once per key — safe to call from a hot path. */
  warnOnce(key, ...a) {
    if (this._once.has(key)) return;
    this._once.add(key);
    this.warn(...a);
  }

  clear() {
    this.buffer.length = 0;
    this._once.clear();
  }
}

function stringify(v) {
  if (typeof v === 'string') return v;
  if (v instanceof Error) return `${v.name}: ${v.message}`;
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}
